import { useState } from "react";
import { Sparkles, Clock, X, Sliders } from "lucide-react";
import { supabase } from "../lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { SliderPopover } from "@/components/ui/slider-popover";

interface SoundGeneratorProps {
  selectedProjectId: string;
  selectedFolderId: string | null;
  onRefreshSounds: (
    isNewSoundGeneration?: boolean,
    generatedSoundId?: string,
  ) => Promise<void>;
  onShowSubscriptionDialog?: () => void;
}

export function SoundGenerator({
  selectedProjectId,
  selectedFolderId,
  onRefreshSounds,
  onShowSubscriptionDialog,
}: SoundGeneratorProps) {
  const [duration, setDuration] = useState(7.0);
  const [promptInfluence, setPromptInfluence] = useState(0.3);
  const [isGenerating, setIsGenerating] = useState(false);
  const [promptText, setPromptText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [activeSlider, setActiveSlider] = useState<
    "duration" | "influence" | null
  >(null);

  const handleGenerateClick = async () => {
    if (isGenerating || !promptText.trim()) return;

    // Check if a project is selected
    if (!selectedProjectId) {
      setError("Please select a project first");
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      // Get the current session for authentication
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error("Please sign in to generate sound effects");
      }

      // Call the existing generate-sound API
      const response = await fetch("/api/generate-sound", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          description: promptText,
          projectId: selectedProjectId,
          folderId: selectedFolderId,
          duration: duration,
          promptInfluence: promptInfluence,
          name: promptText.substring(0, 100),
        }),
      });

      if (!response.ok) {
        // Check for 403 with LIMIT_REACHED
        if (response.status === 403) {
          try {
            const errorData = await response.json();
            if (
              errorData.details === "LIMIT_REACHED" &&
              onShowSubscriptionDialog
            ) {
              onShowSubscriptionDialog();
              return;
            }
          } catch (parseError) {
            // Continue with regular error handling
          }
        }

        let errorMessage = "Failed to generate sound effect";
        try {
          const errorData = await response.json();
          errorMessage = errorData.error ?? errorMessage;
        } catch (parseError) {}
        throw new Error(errorMessage);
      }

      // Sound effect generated and stored successfully
      const result = await response.json();

      // Clear the prompt after successful generation
      setPromptText("");

      // Refresh the sounds list to show the new sound effect with animation
      await onRefreshSounds(true, result.sound?.id);
    } catch (error) {
      setError(
        error instanceof Error ? error.message : "An unknown error occurred",
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const formatDuration = (value: number) => {
    return `${value.toFixed(1)}s`;
  };

  const formatPromptInfluence = (value: number) => {
    return `${Math.round(value * 100)}%`;
  };

  const handleSliderOpenChange = (
    slider: "duration" | "influence",
    open: boolean,
  ) => {
    if (open) {
      setActiveSlider(slider);
    } else if (activeSlider === slider) {
      setActiveSlider(null);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Error Display */}
      {error && (
        <Alert variant="destructive" className="mb-4 animate-slide-in-from-top">
          <AlertDescription className="flex items-center justify-between">
            <span>{error}</span>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setError(null)}
              className="h-auto w-auto p-1 text-destructive/80 hover:text-destructive"
            >
              <X size={16} />
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Two-Row Sound Generator */}
      <div className="border border-border rounded-lg bg-card">
        {/* Top Row: Input Field */}
        <div className="px-3 pt-3 pb-1">
          <Input
            type="text"
            value={promptText}
            onChange={(e) => setPromptText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleGenerateClick();
              }
            }}
            className="w-full bg-transparent border-none shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 text-sm"
            placeholder="Describe a sound..."
            disabled={isGenerating}
          />
        </div>

        {/* Bottom Row: Controls, Character Count, Generate Button */}
        <div className="flex items-center px-3 pt-1 pb-3">
          {/* Combined Duration and Prompt Influence Controls */}
          <div className="flex items-center space-x-1 mr-4">
            <SliderPopover
              icon={Clock}
              value={duration}
              onValueChange={setDuration}
              min={0.5}
              max={22}
              step={0.1}
              label="Duration"
              formatValue={formatDuration}
              minLabel="0.5s"
              maxLabel="22s"
              onOpenChange={(open) => handleSliderOpenChange("duration", open)}
              forceClose={activeSlider !== "duration" && activeSlider !== null}
              className="text-sm px-2 py-1 bg-transparent hover:bg-muted/20 rounded text-muted-foreground"
              showIcon={true}
              compact={true}
            />

            <SliderPopover
              icon={Sliders}
              value={promptInfluence}
              onValueChange={setPromptInfluence}
              min={0}
              max={1}
              step={0.01}
              label="Prompt Influence"
              formatValue={formatPromptInfluence}
              minLabel="Creative"
              maxLabel="Strict"
              description="Controls how closely the AI follows your prompt. Lower values allow more creative interpretation."
              onOpenChange={(open) => handleSliderOpenChange("influence", open)}
              forceClose={activeSlider !== "influence" && activeSlider !== null}
              className="text-sm px-2 py-1 bg-transparent hover:bg-muted/20 rounded text-muted-foreground"
              showIcon={true}
              compact={true}
            />
          </div>

          {/* Spacer */}
          <div className="flex-1"></div>

          {/* Character count */}
          <div className="text-xs text-muted-foreground mr-4">
            {promptText.length} / 201,102
          </div>

          {/* Generate Button */}
          <Button
            onClick={handleGenerateClick}
            disabled={!promptText.trim() || isGenerating}
            size="sm"
            className="px-3 py-1.5"
          >
            {isGenerating ? (
              <>
                <div className="w-3 h-3 mr-1 rounded-full flex items-center justify-center">
                  <Sparkles className="w-3 h-3 animate-pulse" />
                </div>
                <span className="text-sm">Generate</span>
              </>
            ) : (
              <>
                <svg
                  className="w-3 h-3 mr-1"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z" />
                </svg>
                <span className="text-sm">Generate</span>
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
